import { useQuery } from "@apollo/client";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { FC, useMemo, useState, useRef } from "react";
import { ClusteredColumnChart } from "../../../../../../components/charts/ClusteredColumnChart/ClusteredColumnChart";
import { ClusteredColumnChartDataItem } from "../../../../../../components/charts/ClusteredColumnChart/types";
import { BarChartColors } from "../../../../../../components/charts/constants";
import {
  DEFAULT_GROUP_BY,
  DEFAULT_ORDER_BY,
  EMPLOYEES_RANGE,
  REVENUE_RANGE,
} from "./constants";
import { TECHNOLOGY_MARKET_SHARE_QUERY } from "./queries";
import {
  TechnologyMarketSharePayload,
  TechnologyMarketShareVariables,
} from "./types";
import { processChartData } from "./utils";
import useMatchProductsById from "../../hooks/useMatchProductsById";
import { ChartWrapper } from "../../../../../../components/ChartWrapper";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import {
  determineChartContent,
  generateChartSegmentNamesForCSV,
  hasNoData,
} from "../../../../utils";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { DetectionSegment } from "../../hooks/useProductDetectionsBySegment";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { CHART_CONSTANTS } from "../../../constants";

const { baseId, tooltip, chartTitle } =
  CHART_CONSTANTS.keyPlayerMarketPenetrationBySegment;

interface KeyPlayerMarketPenetrationBySegmentProps {
  isLoading: boolean;
  topProductIds: number[];
}

export const KeyPlayerMarketPenetrationBySegment: FC<
  KeyPlayerMarketPenetrationBySegmentProps
> = (props) => {
  const [selectedSegment, setSelectedSegment] = useState("COUNTRY");
  const chartRef = useRef<am4charts.XYChart>(null);
  const combinedFilters = useCombinedFilters();
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );
  const { productsNameMapById } = useMatchProductsById(
    props.topProductIds ?? []
  );
  function handleSegmentSelect(event: SelectChangeEvent) {
    setSelectedSegment(event.target.value);
  }

  const ranges = useMemo(() => {
    if (selectedSegment === "REVENUE") {
      return REVENUE_RANGE;
    }

    if (selectedSegment === "EMPLOYEES") {
      return EMPLOYEES_RANGE;
    }
  }, [selectedSegment]);

  const { data: queryData, loading } = useQuery<
    TechnologyMarketSharePayload,
    TechnologyMarketShareVariables
  >(TECHNOLOGY_MARKET_SHARE_QUERY, {
    variables: {
      productIds: props.topProductIds,
      compareAcross: selectedSegment,
      companyFilters: combinedFilters || {},
      orderBy: DEFAULT_ORDER_BY,
      groupBy: DEFAULT_GROUP_BY,
      ranges,
      localizeInstalls,
      companyMatchFile:
        combinedFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
    },
    fetchPolicy: "no-cache",
    skip: props.isLoading || props.topProductIds.length === 0,
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
  });

  const chartData = useMemo<ClusteredColumnChartDataItem[]>(() => {
    const sourceData = queryData?.technologyMarketShare || [];
    return processChartData(sourceData, selectedSegment, productsNameMapById);
  }, [productsNameMapById, queryData?.technologyMarketShare, selectedSegment]);

  const isLoading = loading || props.isLoading;

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [isLoading, chartData]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <ClusteredColumnChart
          id={`${baseId}-chart`}
          chartData={chartData}
          baseColor={BarChartColors}
          ref={chartRef}
          showPercentageSymbol
          yAxisLabel="Market Penetration (%)"
          showCustomTooltip
        />
      ),
    });
  }, [chartData, noDataReturned, isLoading]);

  const csvFieldMappings = useMemo(() => {
    const fieldMappings: Record<string, string> = {
      category: generateChartSegmentNamesForCSV(selectedSegment),
    };
    if (chartData.length > 0) {
      const uniqueProducts = new Set<string>();
      chartData.forEach((item) => {
        item.cluster.forEach((product) => {
          uniqueProducts.add(product.name);
        });
      });

      uniqueProducts.forEach((productName) => {
        fieldMappings[productName] = `${productName} (% Market Penetration)`;
        fieldMappings[`${productName}_count`] =
          `${productName} (# of Companies)`;
      });
    }

    return fieldMappings;
  }, [chartData, selectedSegment]);
  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned}
      isLoading={isLoading}
      titleExtras={
        <FormControl size="small" sx={{ minWidth: 180, maxWidth: "100%" }}>
          <Select
            id={`${baseId}-select`}
            value={selectedSegment}
            onChange={handleSegmentSelect}
          >
            <MenuItem value={`${DetectionSegment.COUNTRY}`}>
              Segment: Country
            </MenuItem>
            <MenuItem value={`${DetectionSegment.REGION}`}>
              Segment: Region
            </MenuItem>
            <MenuItem value={`${DetectionSegment.HG_INDUSTRY}`}>
              Segment: HG Industry
            </MenuItem>
            {/* the query expects INDUSTRY as the segment name for naics data*/}
            <MenuItem value={`${DetectionSegment.INDUSTRY}`}>
              Segment: NAICS
            </MenuItem>
            <MenuItem value={`${DetectionSegment.EMPLOYEES}`}>
              Segment: Employee Range
            </MenuItem>
            <MenuItem value={`${DetectionSegment.REVENUE}`}>
              Segment: Revenue Range
            </MenuItem>
          </Select>
        </FormControl>
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
};
